import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Row, Segmented, Space, Statistic,
  Switch, Table, Tag, Tooltip, Typography, message
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined,
  BankOutlined, FileTextOutlined, GoldOutlined,
  CheckCircleTwoTone, CloseCircleTwoTone
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import { formatINR, shortDate } from './accountReportsConstants';
import { safeGroups, groupLabel, groupSubtitle } from './reportGroupingHelpers';
// Stage 6B.9H — friendly error / empty message helpers.
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Balance Sheet page (Stage 6B.7E).
//
// Read-only statement of financial position. Three sections — Assets,
// Liabilities, Equity — pulled from `ledger_accounts.current_balance`
// with magnitudes flipped to operator-positive. The "Current Period
// Profit / Loss" line is computed from posted income/expense in the
// (optional) date range and added to the equity side conceptually so
// the accounting identity holds:
//
//   Assets = Liabilities + Equity (±) CurrentPeriodResult
//
// `asOfDate` is intentionally INFORMATIONAL today (backend echoes it
// but `meta.asOfDateApplied=false` — period-cut snapshots need a
// ledger_balance_history table, which is out of scope).
// ─────────────────────────────────────────────────────────────────

// Stage 6B.9G — render a single grouped section (Assets / Liabilities
// / Equity). Each group expands to show its accounts; the section
// total is the sum of group totals (matches summary.totalX).
function GroupedSection({ title, icon, groups, total, columns, emptyText }) {
  const { Text } = Typography;
  if (!groups || groups.length === 0) {
    return (
      <Card size="small" title={<span>{icon}{title}</span>} style={{ marginBottom: 12 }}>
        <Empty description={emptyText} />
      </Card>
    );
  }
  return (
    <Card size="small" title={<span>{icon}{title}</span>} style={{ marginBottom: 12 }}>
      {groups.map((g) => (
        <Card
          key={`${g.accountSubtype || ''}::${g.accountGroup || ''}`}
          size="small"
          type="inner"
          style={{ marginBottom: 8 }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline' }}>
              <span>
                <Text strong>{groupLabel(g)}</Text>
                {groupSubtitle(g) && groupSubtitle(g) !== groupLabel(g) ? (
                  <Text type="secondary" style={{ fontSize: 11.5, marginLeft: 6 }}>
                    {groupSubtitle(g)}
                  </Text>
                ) : null}
              </span>
              <Text strong>₹ {formatINR(g.total)}</Text>
            </div>
          }
        >
          <Table
            size="small"
            rowKey="accountId"
            columns={columns}
            dataSource={g.accounts}
            pagination={false}
          />
        </Card>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    padding: '6px 10px', borderTop: '1px solid #e2e8f0' }}>
        <Text strong>Total {title}</Text>
        <Text strong>₹ {formatINR(total)}</Text>
      </div>
    </Card>
  );
}

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, sections, columns) {
  const lines = [];
  lines.push(columns.map((c) => csvSafe(c.title)).join(','));
  for (const section of sections) {
    lines.push('');
    lines.push(csvSafe(section.title));
    for (const r of section.rows) {
      lines.push(columns.map((c) =>
        csvSafe(typeof c.value === 'function' ? c.value(r) : r[c.dataIndex])
      ).join(','));
    }
    lines.push(csvSafe(`Total ${section.title}`) + ',,,' + ',' + csvSafe(section.total));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function BalanceSheetPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Stage 6B.9H — preserve the last failure so the page can render an
  // Alert + Retry button instead of just flashing a toast.
  const [loadError, setLoadError] = useState(null);
  const [range, setRange] = useState(null);
  const [asOfDate, setAsOfDate] = useState(null);
  const [includeZero, setIncludeZero] = useState(false);
  // Stage 6B.9G — toggle between Flat (legacy) and Grouped (by
  // account_subtype / account_group) renderings.
  const [view, setView] = useState('flat');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (range && range[0] && range[1]) {
        params.fromDate = range[0].format('YYYY-MM-DD');
        params.toDate   = range[1].format('YYYY-MM-DD');
      }
      if (asOfDate) params.asOfDate = asOfDate.format('YYYY-MM-DD');
      if (includeZero) params.includeZero = 'true';
      const out = await accountReportsApi.balanceSheet(params);
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'balance_sheet'));
    } finally { setLoading(false); }
  }, [range, asOfDate, includeZero]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const assets      = data?.assets      || [];
  const liabilities = data?.liabilities || [];
  const equity      = data?.equity      || [];
  // Stage 6B.9G — grouped sections (account_subtype + account_group).
  const assetGroups      = useMemo(() => safeGroups(data?.assetsSection),      [data]);
  const liabilityGroups  = useMemo(() => safeGroups(data?.liabilitiesSection), [data]);
  const equityGroups     = useMemo(() => safeGroups(data?.equitySection),      [data]);
  const groupedAvailable = !!data?.meta?.groupedViewAvailable
    && (assetGroups.length + liabilityGroups.length + equityGroups.length > 0);
  const summary     = data?.summary || {
    totalAssets: 0, totalLiabilities: 0, totalEquity: 0,
    totalEquityWithResult: 0, totalLiabilitiesAndEquity: 0,
    difference: 0, isBalanced: true
  };
  const cpr  = data?.currentPeriodResult || {
    amount: 0, magnitude: 0, resultType: 'breakeven',
    income: 0, expenses: 0
  };
  const meta = data?.meta || {};

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'accountCode', key: 'accountCode', width: 110,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Account Name', dataIndex: 'accountName', key: 'accountName' },
    { title: 'Group', dataIndex: 'accountGroup', key: 'accountGroup', width: 160,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Amount (₹)', dataIndex: 'amount', key: 'amount', width: 160, align: 'right',
      render: (v) => (
        <Tooltip title={v < 0 ? 'Outlier: opposite-sign balance — review' : ''}>
          <span style={{ color: v < 0 ? '#cf1322' : undefined,
                         fontWeight: v < 0 ? 600 : undefined }}>
            {formatINR(v)}
          </span>
        </Tooltip>
      ) }
  ]), []);

  const csvColumns = useMemo(() => ([
    { title: 'Code',         dataIndex: 'accountCode' },
    { title: 'Account Name', dataIndex: 'accountName' },
    { title: 'Group',        dataIndex: 'accountGroup' },
    { title: 'Type',         dataIndex: 'accountType' },
    { title: 'Amount',       dataIndex: 'amount' }
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', marginBottom: 4 }}>
          <Title level={4} style={{ margin: 0 }}>Balance Sheet</Title>
          <Space>
            <Button size="small" icon={<ReloadOutlined />}
              onClick={fetchReport} loading={loading}>Refresh</Button>
            <Button size="small" icon={<PrinterOutlined />}
              onClick={() => window.print()}>Print</Button>
            <Button size="small" icon={<DownloadOutlined />}
              disabled={!assets.length && !liabilities.length && !equity.length}
              onClick={() => downloadCsv('balance-sheet.csv', [
                { title: 'Assets',      rows: assets,      total: summary.totalAssets },
                { title: 'Liabilities', rows: liabilities, total: summary.totalLiabilities },
                { title: 'Equity',      rows: equity,      total: summary.totalEquity }
              ], csvColumns)}>
              Export CSV
            </Button>
          </Space>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          View assets, liabilities and equity with grouped accounting classification.
        </div>

        {loadError ? (
          <Alert
            type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load Balance Sheet"
            description={friendlyAccountsErrorMessage(loadError, 'balance_sheet')}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport}>
                Retry
              </Button>
            }
          />
        ) : null}

        {/* Header cards */}
        <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total Assets (₹)" value={summary.totalAssets}
                precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total Liabilities (₹)" value={summary.totalLiabilities}
                precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Total Equity (₹)" value={summary.totalEquity}
                precision={2} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                + Current period: ₹{formatINR(cpr.amount)}
              </div>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <div style={{ fontSize: 11.5, color: '#64748b',
                            textTransform: 'uppercase', letterSpacing: 0.2,
                            fontWeight: 500 }}>Status</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: '#0f172a' }}>
                {summary.isBalanced ? (
                  <span><CheckCircleTwoTone twoToneColor="#52c41a" /> Balanced</span>
                ) : (
                  <span><CloseCircleTwoTone twoToneColor="#cf1322" /> Not Balanced</span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                Diff ₹{formatINR(Math.abs(summary.difference))}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            {/* Stage 6B.9G — view toggle. */}
            <Space>
              <Text>View:</Text>
              <Segmented
                size="small"
                value={view}
                onChange={setView}
                options={[
                  { label: 'Flat',    value: 'flat' },
                  { label: 'Grouped', value: 'grouped', disabled: !groupedAvailable }
                ]}
              />
            </Space>
            {/* Stage 6B.9G — as-of-date is now applied server-side. */}
            <Space>
              <Text>As of date:</Text>
              <DatePicker size="small" value={asOfDate} onChange={setAsOfDate}
                allowClear format="YYYY-MM-DD" />
            </Space>
            <Space>
              <Text>Current period range:</Text>
              <RangePicker size="small" value={range} onChange={setRange}
                allowClear format="YYYY-MM-DD" />
            </Space>
            <Space>
              <Text>Include zero rows:</Text>
              <Switch size="small" checked={includeZero} onChange={setIncludeZero} />
            </Space>
          </Space>
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              {meta.asOfDateApplied
                ? 'Snapshot view — balances are computed from posted vouchers up to the chosen as-of date.'
                : 'Live balances as of now. Pick an as-of date to compute a snapshot from posted vouchers. The date range scopes the “Current Period Profit / Loss” line.'}
            </Text>
          </div>
        </Card>

        {/* Stage 6B.9G — Grouped view renders Assets/Liabilities/Equity
            using the secondary grouping; flat view keeps the legacy
            tabular layout below it. We render one OR the other, not both. */}
        {view === 'grouped' && groupedAvailable ? (
          <>
            <GroupedSection
              title="Assets"
              icon={<BankOutlined style={{ color: '#0369a1', marginRight: 6 }} />}
              groups={assetGroups}
              total={summary.totalAssets}
              columns={columns}
              emptyText="No asset balances"
            />
            <GroupedSection
              title="Liabilities"
              icon={<FileTextOutlined style={{ color: '#b91c1c', marginRight: 6 }} />}
              groups={liabilityGroups}
              total={summary.totalLiabilities}
              columns={columns}
              emptyText="No liability balances"
            />
            <GroupedSection
              title="Equity"
              icon={<GoldOutlined style={{ color: '#a16207', marginRight: 6 }} />}
              groups={equityGroups}
              total={summary.totalEquity}
              columns={columns}
              emptyText="No equity balances"
            />
            {/* Current Period Result strip — separate so it remains
                visible in both views. */}
            <Card size="small" style={{ marginBottom: 12 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text>Current Period {cpr.resultType === 'loss' ? 'Loss' : cpr.resultType === 'profit' ? 'Profit' : 'Result'}
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                      (Income ₹{formatINR(cpr.income)} − Expenses ₹{formatINR(cpr.expenses)})
                    </Text>
                  </Text>
                </Col>
                <Col>
                  <Text style={{
                    color: cpr.resultType === 'loss'   ? '#cf1322'
                         : cpr.resultType === 'profit' ? '#1f7a25' : '#64748b',
                    fontWeight: 600
                  }}>
                    ₹ {formatINR(cpr.amount)}
                  </Text>
                </Col>
              </Row>
            </Card>
          </>
        ) : null}

        {/* Asset section — flat view */}
        {view === 'flat' || !groupedAvailable ? (
        <>
        <Card size="small" title={<span><BankOutlined style={{ color: '#0369a1', marginRight: 6 }} />Assets</span>}
              style={{ marginBottom: 12 }}>
          <Table
            size="small"
            rowKey="accountId"
            loading={loading}
            columns={columns}
            dataSource={assets}
            pagination={false}
            locale={{ emptyText: <Empty description="No asset balances" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Total Assets</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>₹ {formatINR(summary.totalAssets)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>

        {/* Liabilities */}
        <Card size="small" title={<span><FileTextOutlined style={{ color: '#b91c1c', marginRight: 6 }} />Liabilities</span>}
              style={{ marginBottom: 12 }}>
          <Table
            size="small"
            rowKey="accountId"
            loading={loading}
            columns={columns}
            dataSource={liabilities}
            pagination={false}
            locale={{ emptyText: <Empty description="No liability balances" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Total Liabilities</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>₹ {formatINR(summary.totalLiabilities)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>

        {/* Equity */}
        <Card size="small" title={<span><GoldOutlined style={{ color: '#a16207', marginRight: 6 }} />Equity</span>}>
          <Table
            size="small"
            rowKey="accountId"
            loading={loading}
            columns={columns}
            dataSource={equity}
            pagination={false}
            locale={{ emptyText: <Empty description="No equity balances" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Sub-total: Equity (excl. current period)</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>₹ {formatINR(summary.totalEquity)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text>Current Period {cpr.resultType === 'loss' ? 'Loss' : cpr.resultType === 'profit' ? 'Profit' : 'Result'}
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                        (Income ₹{formatINR(cpr.income)} − Expenses ₹{formatINR(cpr.expenses)})
                      </Text>
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text style={{
                      color: cpr.resultType === 'loss'   ? '#cf1322'
                           : cpr.resultType === 'profit' ? '#1f7a25'
                           : '#64748b',
                      fontWeight: 600
                    }}>
                      ₹ {formatINR(cpr.amount)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Total Equity (with current period)</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>₹ {formatINR(summary.totalEquityWithResult)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
        </>
        ) : null}

        {/* Balance check strip */}
        <div style={{ marginTop: 12, padding: '10px 14px',
                      background: summary.isBalanced ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${summary.isBalanced ? '#bbf7d0' : '#fecaca'}`,
                      borderRadius: 4 }}>
          <Row gutter={16} style={{ alignItems: 'center' }}>
            <Col flex="auto">
              <Text style={{ fontSize: 13, color: '#475569' }}>
                Assets ₹{formatINR(summary.totalAssets)} ={' '}
                Liabilities ₹{formatINR(summary.totalLiabilities)} + Equity
                {' '}₹{formatINR(summary.totalEquityWithResult)}
                {' '}={' '}₹{formatINR(summary.totalLiabilitiesAndEquity)}
              </Text>
            </Col>
            <Col>
              {summary.isBalanced ? (
                <Tag color="green" style={{ fontSize: 13, fontWeight: 600, background: 'transparent' }}>
                  <CheckCircleTwoTone twoToneColor="#52c41a" /> Balanced
                </Tag>
              ) : (
                <Tag color="red" style={{ fontSize: 13, fontWeight: 600, background: 'transparent' }}>
                  <CloseCircleTwoTone twoToneColor="#cf1322" /> Difference ₹{formatINR(summary.difference)}
                </Tag>
              )}
            </Col>
          </Row>
          {!summary.isBalanced ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="Balance Sheet does not balance"
              description="The accounting identity (Assets = Liabilities + Equity + Current Period Result) is broken. Run the accounting verifier and review Trial Balance / Ledger Statement to identify the drifted ledger. Do not auto-fix from this surface."
            />
          ) : null}
        </div>

        <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 11.5 }}>
          Basis: {meta.basis || 'ledger_current_balance'} · Sign convention:{' '}
          {meta.signConvention || 'current_balance = debit − credit'} · No PDF/Jasper export by design.
        </div>
      </div>
    </ErpMasterShell>
  );
}
